import { AsyncPipe } from "@angular/common";
import { Component, effect, inject, input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { CreateGroupModalComponent } from "@components/create-group-modal/create-group-modal.component";
import { ToggleGroupVisibilityButtonComponent } from "@components/toggle-group-visibility-button/toggle-group-visibility-button.component";
import { FloorplanItemReference } from "@domain/project/floorplan-item-reference";
import { Floorplan, ProductTypesInGroup } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { getKindForItem } from "@domain/project/floorplan/floorplan-item.utils";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import { nextGroupId } from "@domain/project/group-id.utils";
import { NavigationService } from "@domain/project/navigation.service";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { DisabledController } from "@odx/angular";
import { FormDirective, FormFieldComponent, FormFieldControlDirective } from "@odx/angular/components/form-field";
import { IconComponent } from "@odx/angular/components/icon";
import { LinkDirective } from "@odx/angular/components/link";
import { ModalService } from "@odx/angular/components/modal";
import { SelectComponent } from "@odx/angular/components/select";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { filter, map, Observable } from "rxjs";

@Component({
  selector: "app-floorplan-item-group",
  standalone: true,
  imports: [
    AsyncPipe,
    IconComponent,
    LinkDirective,
    DisabledController,
    FormDirective,
    FormFieldComponent,
    SelectComponent,
    FormFieldControlDirective,
    ReactiveFormsModule,
    UiKitModule,
    ToggleGroupVisibilityButtonComponent,
  ],
  templateUrl: "./floorplan-item-group.component.html",
  styleUrl: "./floorplan-item-group.component.scss",
})
export class FloorplanItemGroupComponent implements OnInit {
  protected readonly floorplanService = inject(FloorplanService);
  private readonly projectService = inject(ProjectService);
  private readonly userService = inject(UserService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly navigationService = inject(NavigationService);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);

  readonly floorplanItem = input.required<ProductTypesInGroup>();

  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;
  protected floorplan: Floorplan;
  protected groupForm?: FormGroup;
  protected isSelected: boolean = false;
  protected currentGroup?: ProductItemGroup;

  constructor() {
    this.floorplan = this.floorplanService.selectedFloorplan;
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
    effect(() => {
      const item = this.floorplanItem();
      this.currentGroup = this.findProductGroup(item);
      this.groupForm?.reset({ group: this.currentGroup ? this.currentGroup.id : null }, { emitEvent: false });
    });
  }

  ngOnInit(): void {
    this.currentGroup = this.findProductGroup(this.floorplanItem());
    this.initForm();
  }

  protected get isFloorplanItemLocked(): boolean {
    return this.floorplanItem().locked;
  }

  protected checkIsGroupSelected() {
    return this.currentGroup
      ? (this.isSelected = this.floorplanService.selectedFloorplan.floorplanState.isGroupSelected(this.currentGroup.id))
      : false;
  }

  protected createGroup() {
    const nextId = nextGroupId(this.floorplan.project.groups);
    this.modalService.open(CreateGroupModalComponent, { data: { nextGroupId: nextId } }).onClose$.subscribe((result) => {
      const groupData = result as { groupName: string; groupId: number };
      const item = this.floorplanItem();
      const kind = getKindForItem(item);
      if (!kind) return;

      const newGroup = new ProductItemGroup(groupData.groupName, groupData.groupId, [this.floorplan.id]);
      newGroup.addReference(new FloorplanItemReference(this.floorplan.id, item.id, kind));
      this.floorplan.project.addGroup(newGroup);

      if (this.currentGroup) {
        this.currentGroup.removeReference(this.floorplan.id, item.id);
        this.floorplan.project.removeGroupIfEmpty(this.currentGroup);
      }

      const previousGroup = this.currentGroup;
      this.currentGroup = newGroup;
      this.floorplan.publishUpdate(FloorplanEventType.GROUP_PRODUCT_ADDED, {
        product: item,
        targetGroup: newGroup,
        sourceGroup: previousGroup,
      });
    });
  }

  protected addToGroup(formValue: any) {
    const item = this.floorplanItem();
    const kind = getKindForItem(item);
    if (!kind) return;

    const targetGroup = this.floorplan.project.groups.find((g) => g.id === formValue.group);

    if (this.currentGroup) {
      this.currentGroup.removeReference(this.floorplan.id, item.id);
      this.floorplan.project.removeGroupIfEmpty(this.currentGroup);
    }

    if (targetGroup) {
      targetGroup.addReference(new FloorplanItemReference(this.floorplan.id, item.id, kind));
      this.floorplan.publishUpdate(FloorplanEventType.GROUP_PRODUCT_ADDED, {
        product: item,
        targetGroup,
        sourceGroup: this.currentGroup,
      });
      if (this.floorplan.floorplanState.selectedGroupId === targetGroup.id) {
        this.floorplan.floorplanState.selectGroup(targetGroup);
      }
      this.currentGroup = targetGroup;
    } else {
      this.floorplan.publishUpdate(FloorplanEventType.GROUP_PRODUCT_REMOVED, {
        product: item,
        sourceGroup: this.currentGroup,
      });
      this.currentGroup = undefined;
    }
  }

  protected editGroup() {
    const url = this.router.url;
    this.navigationService.toGroupConfiguration(this.floorplanService.selectedFloorplan.id, this.currentGroup!.id, url);
  }

  private initForm() {
    this.groupForm = this.formBuilder.group({ group: [this.currentGroup ? this.currentGroup.id : null] }, { updateOn: "blur" });
    this.groupForm.valueChanges.subscribe((value) => {
      if (!this.groupForm!.valid || this.groupForm!.pristine) return;
      this.addToGroup(value);
    });
  }

  private findProductGroup(item: ProductTypesInGroup): ProductItemGroup | undefined {
    return this.floorplan.project.groups.find((g) =>
      g.references.some((r) => r.floorplanId === this.floorplan.id && r.floorplanProductItemId === item.id),
    );
  }
}
